import { useMemo, useState } from 'react';
import { Plus, Trash2, User, Pencil, Check, X } from 'lucide-react';
import {
  useAppointmentCatalog,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  useDeleteCatalogItem,
  useEmployees,
} from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import type { AppointmentCatalogItem, Skill } from '@/types';
import { SKILL_LABELS } from './AppointmentsPage';

const SKILLS = Object.keys(SKILL_LABELS) as Skill[];

/**
 * Catalogue des prestations sur rendez-vous.
 *
 * La pharmacie démarre sur le catalogue standard (vaccinations, TROD,
 * entretiens, bilans…) et l'adapte : désactiver ce qu'elle ne fait pas, changer
 * une compétence ou une durée, réserver une prestation à une personne, ou créer
 * ses propres prestations.
 */
export default function AppointmentCatalogSection() {
  // `all` : on affiche aussi les prestations désactivées, pour pouvoir les réactiver.
  const { data: items, isLoading } = useAppointmentCatalog(true);
  const { data: employees } = useEmployees();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const groups: { category: string; items: AppointmentCatalogItem[] }[] = [];
    for (const c of items ?? []) {
      const key = c.category ?? 'Autres';
      let g = groups.find((x) => x.category === key);
      if (!g) groups.push((g = { category: key, items: [] }));
      g.items.push(c);
    }
    return groups;
  }, [items]);

  const activeCount = (items ?? []).filter((i) => i.active).length;

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Prestations sur rendez-vous</h2>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {activeCount} prestation{activeCount > 1 ? 's' : ''} active
            {activeCount > 1 ? 's' : ''} · la compétence choisie ici décide si un rendez-vous
            passe au rouge.
          </div>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
          Ajouter
        </Button>
      </div>

      {creating && (
        <div className="mb-3">
          <CatalogForm
            employees={employees ?? []}
            onDone={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      {isLoading && <div className="text-[12px] text-slate-500">Chargement…</div>}

      <div className="space-y-5">
        {grouped.map((g) => (
          <div key={g.category}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              {g.category}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {g.items.map((item) =>
                editingId === item.id ? (
                  <div key={item.id} className="p-3">
                    <CatalogForm
                      item={item}
                      employees={employees ?? []}
                      onDone={() => setEditingId(null)}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : (
                  <CatalogRow
                    key={item.id}
                    item={item}
                    onEdit={() => setEditingId(item.id)}
                  />
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================

function CatalogRow({ item, onEdit }: { item: AppointmentCatalogItem; onEdit: () => void }) {
  const update = useUpdateCatalogItem();
  const del = useDeleteCatalogItem();
  const [note, setNote] = useState<string | null>(null);

  async function remove() {
    if (!confirm(`Supprimer « ${item.label} » ?`)) return;
    const r = await del.mutateAsync(item.id);
    // Une prestation déjà utilisée est désactivée, pas supprimée : sinon on
    // perdrait le libellé des rendez-vous passés.
    if (r.deactivated) {
      setNote(
        r.usedBy > 0
          ? `Utilisée par ${r.usedBy} rendez-vous — désactivée plutôt que supprimée.`
          : 'Prestation du catalogue standard — désactivée plutôt que supprimée.'
      );
    }
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 ${item.active ? '' : 'opacity-50'}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-slate-900 truncate">{item.label}</span>
          {item.isCustom && (
            <Chip variant="default" size="sm">
              Sur mesure
            </Chip>
          )}
          {!item.active && (
            <Chip variant="default" size="sm">
              Désactivée
            </Chip>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="tabular-nums">{item.defaultDurationMinutes} min</span>
          <span>·</span>
          <span>
            {item.requiredSkill
              ? SKILL_LABELS[item.requiredSkill] ?? item.requiredSkill
              : 'aucune compétence requise'}
          </span>
          {/* Qui peut réellement l'assurer. L'habilitation se coche salarié par
              salarié : une prestation sans personne habilitée enverra tous ses
              rendez-vous en alerte, autant le dire ici. */}
          {item.qualifiedCount !== undefined && (
            <>
              <span>·</span>
              <span
                className={item.qualifiedCount === 0 ? 'text-amber-700 font-medium' : 'text-slate-600'}
                title="Se règle depuis la fiche de chaque salarié, onglet Compétences"
              >
                {item.qualifiedCount === 0
                  ? 'personne d\'habilité'
                  : `${item.qualifiedCount} personne${item.qualifiedCount > 1 ? 's' : ''} habilitée${item.qualifiedCount > 1 ? 's' : ''}`}
              </span>
            </>
          )}
          {item.defaultEmployee && (
            <span className="inline-flex items-center gap-1 text-slate-600">
              <User size={11} />
              {item.defaultEmployee.firstName} {item.defaultEmployee.lastName}
            </span>
          )}
        </div>
        {note && <div className="text-[11px] text-amber-700 mt-1">{note}</div>}
      </div>

      <button
        onClick={() => update.mutate({ id: item.id, active: !item.active })}
        className="text-[11px] text-slate-500 hover:text-slate-900 px-2 py-1 rounded-lg hover:bg-slate-100"
      >
        {item.active ? 'Désactiver' : 'Réactiver'}
      </button>
      <button
        onClick={onEdit}
        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        aria-label="Modifier"
      >
        <Pencil size={14} />
      </button>
      <button
        onClick={remove}
        className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
        aria-label="Supprimer"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ============================================================================

function CatalogForm({
  item,
  employees,
  onDone,
  onCancel,
}: {
  item?: AppointmentCatalogItem;
  employees: { id: string; firstName: string; lastName: string }[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(item?.label ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [skill, setSkill] = useState<string>(item?.requiredSkill ?? '');
  const [duration, setDuration] = useState(item?.defaultDurationMinutes ?? 15);
  const [employeeId, setEmployeeId] = useState(item?.defaultEmployeeId ?? '');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateCatalogItem();
  const update = useUpdateCatalogItem();
  const pending = create.isPending || update.isPending;

  async function submit() {
    setError(null);
    const payload = {
      label: label.trim(),
      category: category.trim() || null,
      requiredSkill: (skill || null) as Skill | null,
      defaultDurationMinutes: duration,
      defaultEmployeeId: employeeId || null,
    };
    try {
      if (item) await update.mutateAsync({ id: item.id, ...payload });
      else await create.mutateAsync(payload);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    }
  }

  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]';
  const lbl = 'text-[11px] font-medium text-slate-500 mb-1 block';

  return (
    <div className="bg-white border border-slate-300 rounded-xl p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={lbl}>Libellé</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex. : TROD angine, Bilan nutrition…"
            className={field}
          />
        </div>
        <div>
          <label className={lbl}>Compétence requise</label>
          <select value={skill} onChange={(e) => setSkill(e.target.value)} className={field}>
            <option value="">Aucune</option>
            {SKILLS.map((s) => (
              <option key={s} value={s}>
                {SKILL_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Durée par défaut (min)</label>
          <input
            type="number"
            min={5}
            max={480}
            step={5}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className={field}
          />
        </div>
        <div>
          <label className={lbl}>Catégorie</label>
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Vaccination, TROD…"
            className={field}
          />
        </div>
        <div>
          <label className={lbl}>Réservée à</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className={field}
          >
            <option value="">Personne en particulier</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.firstName} {e.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 mt-2">
        Une prestation réservée n'est proposée qu'à cette personne : si elle est absente, le
        rendez-vous passe au rouge et demande une désignation exceptionnelle.
      </div>

      {error && <div className="text-[11px] text-red-600 mt-2">{error}</div>}

      <div className="flex justify-end gap-2 mt-3">
        <Button variant="default" icon={X} onClick={onCancel}>
          Annuler
        </Button>
        <Button
          variant="primary"
          icon={Check}
          onClick={submit}
          disabled={label.trim().length < 2 || pending}
        >
          {pending ? 'Enregistrement…' : item ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </div>
    </div>
  );
}
